/**
 * Dev-harness session config, encoded in the URL query string so a full
 * test scenario (fixture + quota + gender/side + HR-over-ceiling) is
 * shareable and reproducible without any extra state-management layer.
 * Pure functions, no Phaser/DOM globals touched directly (the DOM harness
 * in `src/harness/DevHarness.ts` calls these against `window.location`).
 */
import type { Side, SessionConfig } from './types';

export const DEFAULT_QUOTA = 3;
export const DEFAULT_SIDE: Side = 'beornings';

/** Harness toggle is framed as male/female (Brief §2/§9 - "male -> chops, female -> plants"); `side`/`gender` are aliases, `side` wins if both are present. */
export function parseSessionConfigFromLocation(search: string): SessionConfig {
  const params = new URLSearchParams(search);

  const sideParam = params.get('side');
  const genderParam = params.get('gender');
  let side: Side = DEFAULT_SIDE;
  if (sideParam === 'beornings' || sideParam === 'lothwen') {
    side = sideParam;
  } else if (genderParam === 'female') {
    side = 'lothwen';
  } else if (genderParam === 'male') {
    side = 'beornings';
  }

  const quotaParam = params.get('quota');
  const parsedQuota = quotaParam !== null ? Number.parseInt(quotaParam, 10) : Number.NaN;
  const quota = Number.isFinite(parsedQuota) && parsedQuota >= 0 ? parsedQuota : DEFAULT_QUOTA;

  const hrOverCeiling = params.get('hrOverCeiling') === 'true';
  const fixturePath = params.get('fixture');

  return { side, quota, hrOverCeiling, fixturePath };
}

/** Inverse of the parser - used by the dev harness's "Apply" button to build a reload URL. */
export function buildLocationSearch(config: SessionConfig): string {
  const params = new URLSearchParams();
  params.set('side', config.side);
  params.set('quota', String(config.quota));
  params.set('hrOverCeiling', String(config.hrOverCeiling));
  if (config.fixturePath) params.set('fixture', config.fixturePath);
  return `?${params.toString()}`;
}
